// src/services/adminBannedIpService.ts

import { apiClient } from "./apiClient";

// ── Types ──────────────────────────────────────────────────────

export interface BannedIpDto {
  id: number;
  ipAddress: string;
  reason: string | null;
  bannedAt: string;
  bannedById: number | null;
  bannedByUsername: string | null;
  expiresAt: string | null;
  isActive: boolean;
  // Username of the user whose last-known IP matches this banned address (null if unknown)
  ownerUsername: string | null;
}

export interface BannedIpPageResponse {
  content: BannedIpDto[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
}

export interface BannedIpFilters {
  search?: string;
  active?: string; // "true" | "false" | "" (all)
  sortBy: string;
  sortDir: "asc" | "desc";
}

export interface BanIpRequest {
  ipAddress: string;
  reason?: string;
  expirationDays?: number | null;
}

const BASE_URL = "/api/admin/banned-ips";

// ── Query builder ──────────────────────────────────────────────

const buildQueryString = (
  page: number,
  size: number,
  filters: BannedIpFilters
): string => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("size", size.toString());
  params.append("sortBy", filters.sortBy);
  params.append("sortDir", filters.sortDir);

  if (filters.search) {
    params.append("search", filters.search);
  }
  if (filters.active) {
    params.append("active", filters.active);
  }

  return params.toString();
};

// ── API calls ──────────────────────────────────────────────────

/**
 * Get paginated list of banned IPs with filters
 */
export const getBannedIps = async (
  page: number = 0,
  size: number = 20,
  filters: BannedIpFilters = { sortBy: "bannedAt", sortDir: "desc" }
): Promise<BannedIpPageResponse> => {
  const queryString = buildQueryString(page, size, filters);
  return apiClient.get<BannedIpPageResponse>(`${BASE_URL}?${queryString}`);
};

/**
 * Ban a new IP address
 */
export const banIp = async (request: BanIpRequest): Promise<BannedIpDto> => {
  return apiClient.post<BannedIpDto>(BASE_URL, request);
};

/**
 * Unban (deactivate) an IP address — ADMIN only
 */
export const unbanIp = async (id: number): Promise<BannedIpDto> => {
  return apiClient.put<BannedIpDto>(`${BASE_URL}/${id}/unban`);
};

/**
 * Permanently delete a banned IP record — ADMIN only
 */
export const deleteBannedIp = async (id: number): Promise<void> => {
  return apiClient.delete<void>(`${BASE_URL}/${id}`);
};